import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button } from '@mui/material'
import { Theme } from '@mui/material/styles'
import EditNoteIcon from '@mui/icons-material/EditNote'
import { GridCellParams } from '@mui/x-data-grid-premium'
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import {
  daysOfWeek,
  generateTimeSlots,
} from 'Features/SchedulerView/SchedulerView.types'
import { TimeHeader } from 'Features/SchedulerView/CommonComponents'
import { slotColumnCommonFields } from 'Features/SchedulerView/SchedulerView.KitchenView'
import {
  SheduleTableProps,
  SchedulerResourceCapacityProfileRowType,
} from './Scheduler.types'
import { ResourceCapacityBulkEdit } from './ResourceCapacityBulkEdit.view'

/**
 * One week, to the hour.
 *
 * Fixed at 168 rather than following the schedule's horizon — the one
 * deliberate difference from the output grid this table mirrors
 * (`SchedulerView.ConstraintsView`, which widens past 168h and suffixes its
 * day groups `(W2)`/`(W3)`). That grid shows one continuous solve; this one is
 * stored per week at `Week{N}/ResourceCapacityProfileDelta`, so a longer
 * horizon means more week folders, not wider rows.
 */
export const PROFILE_HOURS = 168

/** Stored column name for an hour: `h0`..`h167`, matching the Delta table. */
export const hourField = (hour: number) => `h${hour}`

const timeSlots = generateTimeSlots()

/** Pinned identity columns. Module-level so the array is not a new prop
 *  on every render of a grid with 168 columns to reconcile. */
const PINNED_LEFT = ['ResourceConstraint', 'constraintType', 'flat']

const INITIAL_STATE = {
  pinnedColumns: { left: PINNED_LEFT, right: [] },
  pagination: { paginationModel: { pageSize: 50 } },
}

/** Every hour is optional: a CSV naming only the hours that differ from the
 *  flat amount is the intended way to author a shift pattern, rather than
 *  typing 168 cells. */
const UPLOAD_DATA_CONFIG = {
  required: ['ResourceConstraint'],
  optional: Array.from({ length: PROFILE_HOURS }, (_unused, h) => hourField(h)),
}

/** The grid's styles, declared once. As an inline object this is a fresh
 *  `sx` on every render, and emotion re-serialises the whole sheet each
 *  time — on a grid this wide that is felt on every selection click. */
const GRID_SX = {
  maxWidth: 'calc(100vw - 70px)',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: (theme: Theme) => theme.palette.grey[100],
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  '& .MuiDataGrid-cell': {
    paddingLeft: 0,
    paddingRight: 0,
    borderRight: 'none',
  },
  '& .MuiDataGrid-cell--editable': { backgroundColor: '#EEF2F7' },
  // Resolved once here rather than per cell — see the column's
  // `cellClassName`. An authored zero is the one entry that takes a resource
  // away entirely, so it reads as a stop, not a small number; an inherited
  // cell stays visibly secondary to it.
  '& .cap-authored': { fontWeight: 700 },
  '& .cap-zero': { fontWeight: 700, color: '#d32f2f' },
  '& .cap-inherited': {
    color: (theme: Theme) => theme.palette.text.disabled,
    fontStyle: 'italic',
  },
  '& .MuiDataGrid-cell--pinnedLeft': {
    backgroundColor: (theme: Theme) => theme.palette.background.paper,
    borderRight: (theme: Theme) => `1px solid ${theme.palette.divider}`,
  },
  '& .MuiDataGrid-virtualScroller': { paddingBottom: '15px' },
}

/**
 * Day-grouped hourly columns, Mon..Sun. Same construction as the output grid's
 * `generateColumnGroupingModel`, minus the multi-week cycling — see
 * `PROFILE_HOURS`.
 */
const buildColumnGroupingModel = () =>
  daysOfWeek.map((day, dayIndex) => ({
    groupId: `day-${dayIndex}`,
    headerName: day,
    children: timeSlots.map((_time, hourIndex) => ({
      field: hourField(dayIndex * 24 + hourIndex),
    })),
  }))

type ProfileTableProps =
  SheduleTableProps<SchedulerResourceCapacityProfileRowType> & {
    /**
     * `ResourceConstraint` → the flat `ConstraintAmount` an unset hour
     * inherits, and the constraint's type.
     *
     * Passed as lookups rather than folded into the rows on purpose: both live
     * on `ResourceAndMaterialConstraintsDelta`, and a row field would be posted
     * back on save and written into this table too, leaving one resource with
     * two copies of its amount that can disagree. The output grid keeps its
     * `Limit` column out of its rows for the same reason.
     */
    flatAmounts: Record<string, number>
    constraintTypes: Record<string, string>
  }

export const SchedulerResourceCapacityProfileTable = ({
  data,
  isLoading,
  updateTableData,
  uploadScheduleData,
  isUploading,
  isSaving,
  updateRowsStatus,
  predicate,
  flatAmounts,
  constraintTypes,
  readOnly = false,
}: ProfileTableProps) => {
  const columnGroupingModel = useMemo(() => buildColumnGroupingModel(), [])

  // Stable identity: an inline arrow here is a new prop on every render of
  // a grid that already has 168 columns to reconcile.
  const isCellEditable = useCallback(
    (params: GridCellParams) => !readOnly && params.field.startsWith('h'),
    [readOnly]
  )

  // ── Bulk edit ────────────────────────────────────────────────────────
  //
  // 168 cells per resource is not something anyone types. The dialog turns
  // a shift pattern into one action; this half is the plumbing that tells
  // it which resources to act on and writes the result back.
  const [bulkOpen, setBulkOpen] = useState(false)
  const [selectedResources, setSelectedResources] = useState<string[]>([])
  const gridApiRef = useRef<GridApiPremium | null>(null)

  // `BaseTable` owns its selection model and exposes no change callback, so
  // the count is read off the grid's own API. Subscribed rather than polled
  // so the button label tracks the ticks as they happen.
  const readSelection = useCallback(() => {
    const api = gridApiRef.current
    if (!api) return
    const names: string[] = []
    api.getSelectedRows().forEach((row) => {
      const name = (row as SchedulerResourceCapacityProfileRowType)
        .ResourceConstraint
      if (name) names.push(String(name))
    })
    // Only re-render when the selection genuinely changed. A fresh array
    // every time `rowSelectionChange` fires re-renders the whole grid —
    // 168 columns of it — for no change at all.
    setSelectedResources((prev) =>
      prev.length === names.length && prev.every((n, i) => n === names[i])
        ? prev
        : names
    )
  }, [])

  useEffect(() => {
    const api = gridApiRef.current
    if (!api?.subscribeEvent) return
    const unsubscribe = api.subscribeEvent('rowSelectionChange', readSelection)
    return unsubscribe
  }, [readSelection, data])

  /**
   * Write `value` into every selected resource's `hours`.
   *
   * `null` clears an hour back to inheriting the flat amount — which is why
   * the field is set to null rather than deleted: the row is posted as a
   * whole, so an absent key would leave the old value standing in Delta.
   */
  const applyBulk = useCallback(
    (hours: number[], value: number | null) => {
      const chosen = new Set(selectedResources)
      const changed: Record<number, SchedulerResourceCapacityProfileRowType> =
        {}
      data.forEach((row) => {
        if (!chosen.has(String(row.ResourceConstraint))) return
        const next = { ...row }
        hours.forEach((h) => {
          next[hourField(h)] = value
        })
        changed[row.id] = next
      })
      if (Object.keys(changed).length > 0) updateTableData(changed)
    },
    [data, selectedResources, updateTableData]
  )

  const columns: ExtendedGridColDef[] = useMemo(() => {
    const cols: ExtendedGridColDef[] = [
      {
        field: 'ResourceConstraint',
        headerName: 'Resource',
        width: 150,
        headerAlign: 'center',
      },
      {
        field: 'constraintType',
        headerName: 'Type',
        width: 90,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (
          _: unknown,
          row: SchedulerResourceCapacityProfileRowType
        ) => constraintTypes[row.ResourceConstraint] ?? '-',
      },
      {
        field: 'flat',
        headerName: 'Flat',
        width: 70,
        align: 'center',
        headerAlign: 'center',
        description:
          'The single ConstraintAmount from Resource & Material Constraints. Every hour left blank below inherits it.',
        valueGetter: (
          _: unknown,
          row: SchedulerResourceCapacityProfileRowType
        ) => flatAmounts[row.ResourceConstraint] ?? '-',
      },
    ]

    for (let hour = 0; hour < PROFILE_HOURS; hour++) {
      const label = timeSlots[hour % 24]
      cols.push({
        field: hourField(hour),
        headerName: label,
        width: 50,
        type: 'number',
        editable: !readOnly,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'time-column',
        renderHeader: () => <TimeHeader time={label} />,
        // An emptied cell must come back as null, not 0: null means "inherit
        // the flat amount", 0 means "unavailable", and the two are different.
        // Without this a cleared numeric cell reaches the row as an empty
        // string, the service writes it as a real value, and the planner has
        // no way back to "inherit".
        valueParser: (value: unknown) =>
          value === '' || value === null || value === undefined
            ? null
            : Number(value),
        // **No `renderCell`.** With 168 hourly columns, a `renderCell` mounts
        // a React element per visible cell, and an `sx` object on it runs the
        // style engine per cell per render — about 5,000 serialisations on
        // every re-render, which is what made ticking a row feel slow. A
        // `valueFormatter` returns a string the grid renders as plain text,
        // and the styling moves to a class resolved once in CSS below.
        valueFormatter: (
          value: unknown,
          row: SchedulerResourceCapacityProfileRowType
        ) => {
          if (value !== null && value !== undefined && value !== '') {
            const n = Number(value)
            return Number.isFinite(n) ? n.toFixed(1) : ''
          }
          const flat = flatAmounts[row?.ResourceConstraint]
          return flat === undefined || !Number.isFinite(flat)
            ? ''
            : flat.toFixed(1)
        },
        // Authored / authored-zero / inherited. Returning a class is cheap;
        // the rules themselves are declared once on the grid.
        cellClassName: (params: GridCellParams) => {
          const v = params.value
          if (v === null || v === undefined || v === '') return 'cap-inherited'
          return Number(v) === 0 ? 'cap-zero' : 'cap-authored'
        },
        ...slotColumnCommonFields,
      })
    }

    return cols
  }, [flatAmounts, constraintTypes, readOnly])

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditNoteIcon />}
          disabled={readOnly || selectedResources.length === 0}
          onClick={() => setBulkOpen(true)}
        >
          Bulk edit
          {selectedResources.length > 0 ? ` (${selectedResources.length})` : ''}
        </Button>
        <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
          {selectedResources.length === 0
            ? 'Tick one or more resources to set a shift pattern in one go.'
            : 'Set a range, a nightly window, or chosen days at once.'}
        </Box>
      </Box>
      <ResourceCapacityBulkEdit
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        resources={selectedResources}
        onApply={applyBulk}
      />
      <BaseTable
        // Each user's saved view of this table (Save as my default).
        layoutKey="ResourceCapacityProfile"
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={updateTableData}
        uploadData={readOnly ? undefined : uploadScheduleData}
        isUploading={isUploading}
        isSaving={isSaving}
        // On, because bulk edit acts on the ticked rows.
        checkBoxSelection={true}
        showTableToolBar={true}
        rowHeight={40}
        downloadFileName="SchedulerResourceCapacityProfile"
        columnGroupingModel={columnGroupingModel}
        apiRefCallback={(ref) => {
          gridApiRef.current = ref.current
          readSelection()
        }}
        isCellEditable={isCellEditable}
        // `BaseTable` has no readOnly prop of its own — the sibling constraints
        // grid passes one and it does nothing — so read-only is enforced here:
        // `editable: false` on every hour column above, `isCellEditable` as a
        // second gate, and the whole upload path withdrawn by omitting
        // `multiStageUpload` rather than by disabling it.
        multiStageUpload={
          readOnly
            ? undefined
            : {
                uploadFile: uploadScheduleData,
                isUploading: isUploading,
                columns: columns,
                updateRowsStatus: updateRowsStatus,
                originalData: data,
                predicate: predicate,
                dataConfig: UPLOAD_DATA_CONFIG,
              }
        }
        initialState={INITIAL_STATE}
        pinnedColumns={PINNED_LEFT}
        sx={GRID_SX}
        tableHeight={560}
      />
    </Box>
  )
}
